/**
 * Timbrado e folha de impressão alinhados aos Recibos (logo, cores, tipografia).
 * Usado em impressos agrupados da evolução e documentos clínicos simples.
 */

import {
  documentPrintFontScale,
  scaleDocumentPrintFontSizes,
} from './clinicDocumentPrintFont';

export function escapeHtml(s: string | undefined | null): string {
  if (s == null || s === '') return '';
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Timbrado (cabeçalho, logo e rodapé) só é impresso quando a unidade não
 * desativou a opção. Clínicas com papel timbrado pré-impresso desativam para
 * imprimir apenas o conteúdo. Ausência da flag = habilitado (compatibilidade).
 */
export function isClinicLetterheadEnabled(c: any): boolean {
  return c?.letterhead_print_enabled !== false;
}

/**
 * Cor de destaque do timbrado: usa a cor primária configurada pela clínica
 * (identidade visual própria nos documentos). Valida o hex para uso seguro
 * em atributo style; fallback no azul institucional.
 */
export function clinicPrintAccentColor(c: any): string {
  const raw = String(c?.primary_color || '').trim();
  return /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(raw) ? raw : '#2563eb';
}

function hexToRgbTriplet(hex: string): string {
  let h = hex.replace('#', '');
  if (h.length === 3) h = h.split('').map((x) => x + x).join('');
  const n = parseInt(h, 16);
  return `${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}`;
}

/**
 * Variáveis CSS de identidade visual do timbrado (aplicadas no <body>).
 * Os documentos assumem a cor primária da clínica; sem configuração, azul institucional.
 */
export function clinicPrintAccentVarsStyle(c: any): string {
  const accent = clinicPrintAccentColor(c);
  const scale = documentPrintFontScale(c?.document_print_font_size_percent);
  return `--lh-accent:${accent};--lh-accent-rgb:${hexToRgbTriplet(accent)};--doc-fs-scale:${scale}`;
}

/** Espaço superior (mm) reservado no topo da folha. Aceita 0–120mm. */
export function clinicLetterheadTopOffsetMm(c: any): number {
  const n = Number(c?.letterhead_top_offset_mm);
  if (!Number.isFinite(n) || n <= 0) return 0;
  return Math.min(120, Math.round(n));
}

/**
 * Espaçador em branco no topo da folha. Só é aplicado quando o timbrado está
 * desativado (papel pré-impresso), para o conteúdo começar abaixo do timbrado
 * já impresso no papel.
 */
export function buildLetterheadTopSpacerHtml(c: any): string {
  if (isClinicLetterheadEnabled(c)) return '';
  const mm = clinicLetterheadTopOffsetMm(c);
  if (!mm) return '';
  return `<div class="lh-top-spacer" style="height:${mm}mm"></div>`;
}

export type ClinicalPrintPatient = {
  full_name?: string;
  social_name?: string;
  cpf?: string;
  rg?: string;
  birth_date?: string;
  gender?: string;
  phone?: string;
  phone_secondary?: string;
  whatsapp?: string;
  email?: string;
  address_street?: string;
  address_number?: string;
  address_complement?: string;
  address_neighborhood?: string;
  address_city?: string;
  address_state?: string;
  address_zip?: string;
  insurance_plan?: string;
  insurance_number?: string;
  insurance_validity?: string;
  clinic_insurances?: { name?: string } | null;
};

function printPatientFmtDate(d?: string | null): string {
  if (!d) return '';
  try {
    return new Date(String(d).split('T')[0] + 'T12:00:00').toLocaleDateString('pt-BR');
  } catch {
    return String(d);
  }
}

function printPatientAgeText(birthDate?: string | null): string {
  if (!birthDate) return '';
  try {
    const birth = new Date(String(birthDate).split('T')[0] + 'T12:00:00');
    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();
    const m = today.getMonth() - birth.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age -= 1;
    return age >= 0 ? `${age} anos` : '';
  } catch {
    return '';
  }
}

function printPatientGenderLabel(g?: string | null): string {
  const map: Record<string, string> = {
    masculine: 'Masculino',
    feminine: 'Feminino',
    male: 'Masculino',
    female: 'Feminino',
    other: 'Outro',
    prefer_not_say: 'Prefere não informar',
  };
  return map[String(g || '').toLowerCase()] || String(g || '');
}

/** Bloco de identificação do paciente para impressos clínicos (compacto ou detalhado, estilo atestado). */
export function buildClinicalPrintPatientInfoHtml(
  patient?: ClinicalPrintPatient | null,
  opts?: { variant?: 'compact' | 'detailed'; issueDate?: string | Date | null },
): string {
  if (!patient?.full_name) return '';

  const variant = opts?.variant || 'compact';

  if (variant === 'compact') {
    const birth = patient.birth_date ? printPatientFmtDate(patient.birth_date) : '';
    return `<div class="patient-info"><strong>Paciente:</strong> ${escapeHtml(patient.full_name)}${patient.cpf ? ` &nbsp;&nbsp; <strong>CPF:</strong> ${escapeHtml(String(patient.cpf))}` : ''}${birth ? ` &nbsp;&nbsp; <strong>Nascimento:</strong> ${escapeHtml(birth)}` : ''}</div>`;
  }

  const nameLine =
    patient.social_name && patient.social_name !== patient.full_name
      ? `${patient.full_name} (nome social: ${patient.social_name})`
      : patient.full_name;

  const rows: string[] = [
    `<div class="patient-info-row patient-info-row--full"><strong>Paciente:</strong> ${escapeHtml(nameLine)}</div>`,
  ];

  if (patient.cpf) rows.push(`<div><strong>CPF:</strong> ${escapeHtml(String(patient.cpf))}</div>`);
  if (patient.rg) rows.push(`<div><strong>RG:</strong> ${escapeHtml(String(patient.rg))}</div>`);

  if (patient.birth_date) {
    const age = printPatientAgeText(patient.birth_date);
    rows.push(
      `<div><strong>Nascimento:</strong> ${escapeHtml(printPatientFmtDate(patient.birth_date))}${age ? ` (${escapeHtml(age)})` : ''}</div>`,
    );
  }

  if (patient.gender) {
    rows.push(`<div><strong>Sexo:</strong> ${escapeHtml(printPatientGenderLabel(patient.gender))}</div>`);
  }

  if (opts?.issueDate) {
    const d =
      opts.issueDate instanceof Date
        ? opts.issueDate
        : new Date(String(opts.issueDate).split('T')[0] + 'T12:00:00');
    if (!Number.isNaN(d.getTime())) {
      rows.push(`<div><strong>Data:</strong> ${escapeHtml(d.toLocaleDateString('pt-BR'))}</div>`);
    }
  }

  return `<div class="patient-info patient-info--detailed">
  <div class="patient-info-heading">Identificação do paciente</div>
  <div class="patient-info-grid">${rows.join('')}</div>
</div>`;
}

/** Cabeçalho com nome da clínica ao centro (Recibos, Relatórios). */
export function buildClinicNameLetterheadBlockHtml(c: any): string {
  if (!c) {
    return `
      <div class="doc-head-logo doc-head-logo--empty"></div>
      <div class="doc-head-center">
        <div class="clinic-name">Clínica</div>
        <div class="clinic-subtitle">Configure nome, endereço e logo em Configurações da Clínica</div>
      </div>
      <div class="doc-head-meta"></div>`;
  }
  const logo = c.logo_url
    ? `<div class="doc-head-logo"><img src="${escapeHtml(c.logo_url)}" alt="" /></div>`
    : '<div class="doc-head-logo doc-head-logo--empty"></div>';
  const subtitle = c.clinic_subtitle
    ? `<div class="clinic-subtitle">${escapeHtml(c.clinic_subtitle)}</div>`
    : '';
  const cnpjLine = c.cnpj ? `<div>CNPJ: ${escapeHtml(c.cnpj)}</div>` : '';
  let addr = '';
  if (c.address_street) {
    addr = `<div>${escapeHtml(c.address_street)}${c.address_number ? `, ${escapeHtml(c.address_number)}` : ''}${c.address_neighborhood ? ` — ${escapeHtml(c.address_neighborhood)}` : ''}</div>`;
  }
  const cityLine =
    c.address_city || c.address_state || c.address_zip
      ? `<div>${[c.address_city, c.address_state].filter(Boolean).map(escapeHtml).join(' - ')}${c.address_zip ? ` • CEP ${escapeHtml(c.address_zip)}` : ''}</div>`
      : '';
  const tel =
    c.phone || c.whatsapp ? `<div>Tel: ${escapeHtml(c.phone || c.whatsapp)}</div>` : '';
  const mail = c.email ? `<div>${escapeHtml(c.email)}</div>` : '';
  const site = c.website ? `<div>${escapeHtml(c.website)}</div>` : '';

  return `
    ${logo}
    <div class="doc-head-center">
      <div class="clinic-name">${escapeHtml(c.clinic_name || 'Clínica')}</div>
      ${subtitle}
    </div>
    <div class="doc-head-meta">
      ${cnpjLine}
      ${addr}
      ${cityLine}
      ${tel}
      ${mail}
      ${site}
    </div>`;
}

/** Bloco interno do cabeçalho (logo + centro + meta) — mesmo padrão de Recibos.tsx */
export function buildClinicLetterheadBlockHtml(c: any): string {
  if (!c) {
    return `
      <div class="doc-head-logo doc-head-logo--empty"></div>
      <div class="doc-head-center">
        <div class="clinic-name">Clínica</div>
        <div class="clinic-subtitle">Configure nome, endereço e logo em Configurações da Clínica</div>
      </div>
      <div class="doc-head-meta"></div>`;
  }
  const logo = c.logo_url
    ? `<div class="doc-head-logo"><img src="${escapeHtml(c.logo_url)}" alt="" /></div>`
    : '<div class="doc-head-logo doc-head-logo--empty"></div>';
  // Centro do cabeçalho: apenas profissionais em destaque (timbrado). Sem profissional, fica vazio
  // — logo e dados de contato à direita já identificam a clínica.
  const center = buildLetterheadProfessionalsHtml(c);
  const cnpjLine = c.cnpj ? `<div>CNPJ: ${escapeHtml(c.cnpj)}</div>` : '';
  let addr = '';
  if (c.address_street) {
    addr = `<div>${escapeHtml(c.address_street)}${c.address_number ? `, ${escapeHtml(c.address_number)}` : ''}${c.address_neighborhood ? ` — ${escapeHtml(c.address_neighborhood)}` : ''}</div>`;
  }
  const cityLine =
    c.address_city || c.address_state || c.address_zip
      ? `<div>${[c.address_city, c.address_state].filter(Boolean).map(escapeHtml).join(' - ')}${c.address_zip ? ` • CEP ${escapeHtml(c.address_zip)}` : ''}</div>`
      : '';
  const tel =
    c.phone || c.whatsapp ? `<div>Tel: ${escapeHtml(c.phone || c.whatsapp)}</div>` : '';
  const mail = c.email ? `<div>${escapeHtml(c.email)}</div>` : '';
  const site = c.website ? `<div>${escapeHtml(c.website)}</div>` : '';

  return `
    ${logo}
    <div class="doc-head-center">
      ${center}
    </div>
    <div class="doc-head-meta">
      ${cnpjLine}
      ${addr}
      ${cityLine}
      ${tel}
      ${mail}
      ${site}
    </div>`;
}

/** Linha de profissionais em destaque no cabeçalho (timbrado). */
export function buildLetterheadProfessionalsHtml(c: any): string {
  const list = Array.isArray(c?.letterhead_professionals) ? c.letterhead_professionals : [];
  const items = list
    .filter((p: any) => p && (p.name || p.specialty || p.registration))
    .map(
      (p: any) => `<div class="lh-prof">
        <div class="lh-prof-name">${escapeHtml(p.name)}</div>
        ${p.specialty ? `<div class="lh-prof-sub">${escapeHtml(p.specialty)}</div>` : ''}
        ${p.registration ? `<div class="lh-prof-reg">${escapeHtml(p.registration)}</div>` : ''}
      </div>`,
    )
    .join('');
  if (!items) return '';
  return `<div class="lh-professionals">${items}</div>`;
}

/** Ícones SVG inline do rodapé (telefone, localização, Instagram). Herdam a cor de destaque via currentColor. */
const LH_ICON_PHONE =
  '<svg viewBox="0 0 24 24" width="9" height="9" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z"/></svg>';
const LH_ICON_PIN =
  '<svg viewBox="0 0 24 24" width="9" height="9" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>';
const LH_ICON_INSTA =
  '<svg viewBox="0 0 24 24" width="9" height="9" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="2" width="20" height="20" rx="5" ry="5"/><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/></svg>';

/** Rodapé com contatos por unidade (multi-unidade). Respeita flag por unidade em metadata.show_in_document_footer. */
export function buildLetterheadFooterUnitsHtml(c: any): string {
  if (!isClinicLetterheadEnabled(c)) return '';
  const list = Array.isArray(c?.letterhead_footer_units) ? c.letterhead_footer_units : [];
  const items = list
    .filter((u: any) => u && (u.name || u.phone || u.address || u.instagram))
    .map(
      (u: any) => `<div class="lh-unit">
        ${u.name ? `<div class="lh-unit-name">${escapeHtml(u.name)}</div>` : ''}
        ${u.phone ? `<div class="lh-unit-line"><span class="lh-unit-ico">${LH_ICON_PHONE}</span>${escapeHtml(u.phone)}</div>` : ''}
        ${u.address ? `<div class="lh-unit-line"><span class="lh-unit-ico">${LH_ICON_PIN}</span>${escapeHtml(u.address)}</div>` : ''}
        ${u.instagram ? `<div class="lh-unit-line"><span class="lh-unit-ico">${LH_ICON_INSTA}</span>${escapeHtml(u.instagram)}</div>` : ''}
      </div>`,
    )
    .join('');
  if (!items) return '';
  return `<div class="lh-footer-units">${items}</div>`;
}

export interface ClinicPrintDocumentOptions {
  clinic: any | null;
  browserTitle: string;
  /** Pill central (ex.: Documentos da evolução) */
  badgeText?: string;
  /** Linha discreta abaixo do badge (ex.: data) */
  subtitleBelowBadge?: string;
  bodyHtml: string;
  footerNote?: string;
}

/** Uma folha timbrada no pacote de impressão (sem badge global). */
export interface ClinicPrintSheetSpec {
  bodyHtml: string;
  footerNote?: string;
  /** Cabeçalho/rodapé já inclusos no bodyHtml (ex.: receituário com thead/tfoot). */
  selfContainedLetterhead?: boolean;
}

/** Vários documentos: um timbre por item, cada um começa em nova página na impressão. Sem pill/badge comum. */
export function buildClinicMultiSheetPrintHtml(opts: {
  clinic: any | null;
  browserTitle: string;
  sheets: ClinicPrintSheetSpec[];
}): string {
  if (!opts.sheets.length) {
    return buildClinicPrintDocumentHtml({
      clinic: opts.clinic,
      browserTitle: opts.browserTitle,
      bodyHtml: '',
    });
  }

  const letterheadOn = isClinicLetterheadEnabled(opts.clinic);
  const unitsHtml = buildLetterheadFooterUnitsHtml(opts.clinic);
  const sheetsHtml = opts.sheets
    .map((s, i) => {
      const foot = s.footerNote ? `<div class="footer-note">${escapeHtml(s.footerNote)}</div>` : '';
      const cls = i === 0 ? 'sheet' : 'sheet sheet--page-break';
      const head = s.selfContainedLetterhead
        ? ''
        : letterheadOn
          ? `<div class="clinic-sheet-letterhead">${buildClinicLetterheadBlockHtml(opts.clinic)}</div>`
          : buildLetterheadTopSpacerHtml(opts.clinic);
      const sheetFooterUnits = s.selfContainedLetterhead ? '' : unitsHtml;
      return `<div class="${cls}">
  ${head}
  ${s.bodyHtml}
  ${foot}
  ${sheetFooterUnits}
</div>`;
    })
    .join('\n');

  return `<!DOCTYPE html><html lang="pt-BR"><head><meta charset="utf-8"><title>${escapeHtml(opts.browserTitle)}</title>
<style>${CLINIC_PRINT_BASE_CSS}${MULTI_SHEET_PRINT_EXTRA_CSS}</style></head><body style="${clinicPrintAccentVarsStyle(opts.clinic)}">
${sheetsHtml}
</body></html>`;
}

/** Folha única com timbre — igual filosofia aos Recibos */
export function buildClinicPrintDocumentHtml(opts: ClinicPrintDocumentOptions): string {
  const badge = opts.badgeText
    ? `<div class="doc-badge"><span>${escapeHtml(opts.badgeText)}</span></div>`
    : '';
  const sub = opts.subtitleBelowBadge
    ? `<div class="doc-number">${escapeHtml(opts.subtitleBelowBadge)}</div>`
    : '';
  const foot = opts.footerNote
    ? `<div class="footer-note">${escapeHtml(opts.footerNote)}</div>`
    : '';
  const head = isClinicLetterheadEnabled(opts.clinic)
    ? `<div class="clinic-sheet-letterhead">${buildClinicLetterheadBlockHtml(opts.clinic)}</div>`
    : buildLetterheadTopSpacerHtml(opts.clinic);

  return `<!DOCTYPE html><html lang="pt-BR"><head><meta charset="utf-8"><title>${escapeHtml(opts.browserTitle)}</title>
<style>${CLINIC_PRINT_BASE_CSS}</style></head><body style="${clinicPrintAccentVarsStyle(opts.clinic)}">
<div class="sheet">
  ${head}
  ${badge}
  ${sub}
  ${opts.bodyHtml}
  ${foot}
  ${buildLetterheadFooterUnitsHtml(opts.clinic)}
</div>
</body></html>`;
}

/** Assinatura + data alinhadas à esquerda, com área livre para assinar à mão. Único em combos de impressão (evita conflitar com `.signature-line` dentro de `<style>` de anexos). */
const CLINIC_DOC_SIGNATURE_CSS_RAW = `
  .clinic-doc-sign-zone { margin-top: 32px; padding-top: 12px; page-break-inside: avoid; break-inside: avoid; }
  .clinic-doc-sign-zone--compact { margin-top: 24px; padding-top: 8px; }
  .clinic-doc-sign-row { display: flex; flex-wrap: wrap; gap: 20px 36px; align-items: flex-end; justify-content: flex-start; }
  .clinic-doc-sign-slot { flex: 1 1 220px; min-width: 180px; max-width: 380px; text-align: left; }
  .clinic-doc-sign-slot--narrow { flex: 0 1 180px; min-width: 140px; max-width: 200px; }
  .clinic-doc-sign-spacer { min-height: 32px; margin-bottom: 2px; }
  .clinic-doc-sign-spacer--with-img { min-height: 14px; }
  .clinic-doc-sign-rule { border: none; border-top: 1px solid #1f2937; margin: 0 0 6px 0; width: 100%; max-width: 320px; }
  .clinic-doc-sign-caption { font-size: 10px; margin: 1px 0 0 0; color: #333; line-height: 1.4; text-align: left; }
  .clinic-doc-sign-prof-img-wrap { margin-bottom: 2px; }
  .clinic-doc-sign-prof-img-wrap img { max-height: 72px; max-width: 320px; object-fit: contain; vertical-align: bottom; display: inline-block; }
`;

export const CLINIC_DOC_SIGNATURE_CSS = scaleDocumentPrintFontSizes(CLINIC_DOC_SIGNATURE_CSS_RAW);

/** Um bloco anexo (exames, indicações, laudo…) dentro da mesma folha */
export function buildEvolutionPrintAttachmentSection(
  categoryLabel: string,
  templateName: string,
  bodyInnerHtml: string,
): string {
  return `<div class="attachment-block">
    <div class="attachment-meta">${escapeHtml(categoryLabel)}</div>
    <h2 class="attachment-title">${escapeHtml(templateName)}</h2>
    <div class="attachment-body">${bodyInnerHtml}</div>
  </div>`;
}

const CLINIC_PRINT_BASE_CSS_RAW = `
  :root { --lh-accent: #2563eb; --lh-accent-rgb: 37, 99, 235; }
  * { box-sizing: border-box; }
  body {
    font-family: 'Segoe UI', system-ui, -apple-system, 'Helvetica Neue', sans-serif;
    color: #1e293b; margin: 0; padding: 24px; background: #eef1f6;
    font-feature-settings: 'kern' 1, 'liga' 1;
    -webkit-font-smoothing: antialiased;
  }
  .sheet {
    max-width: 210mm; margin: 0 auto; background: #fff; padding: 34px 40px 40px;
    border-radius: 10px; box-shadow: 0 2px 16px rgba(15,23,42,0.10);
  }
  .clinic-sheet-letterhead {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 24px;
    border-bottom: 1px solid rgba(var(--lh-accent-rgb), 0.30);
    padding-bottom: 18px;
    margin-bottom: 26px;
    page-break-inside: avoid;
    break-inside: avoid;
    position: relative;
  }
  /* Barra curta de destaque sobre o filete — assinatura visual do timbrado. */
  .clinic-sheet-letterhead::after {
    content: ''; position: absolute; left: 0; bottom: -2px; width: 96px; height: 3px;
    background: var(--lh-accent); border-radius: 2px;
  }
  .doc-head-logo { flex-shrink: 0; min-width: 120px; }
  .doc-head-logo img { display: block; max-width: 170px; max-height: 70px; width: auto; height: auto; object-fit: contain; object-position: left top; }
  .doc-head-logo--empty { min-height: 48px; }
  .doc-head-center { flex: 1; min-width: 0; text-align: center; align-self: center; }
  .doc-head-center .lh-professionals { justify-content: flex-start; text-align: left; }
  .doc-head-meta {
    flex-shrink: 0; max-width: 44%; font-size: 9.5px; color: #5b6b80;
    text-align: right; line-height: 1.75; letter-spacing: 0.015em;
    padding-left: 18px; border-left: 1px solid #e8edf3; align-self: stretch;
  }
  .clinic-name {
    font-family: Georgia, 'Times New Roman', serif;
    font-size: 21px; font-weight: 700; color: var(--lh-accent);
    margin: 0; letter-spacing: 0.015em;
  }
  .clinic-subtitle {
    font-size: 9.5px; color: #64748b; margin-top: 5px;
    text-transform: uppercase; letter-spacing: 0.18em; font-weight: 600;
  }
  .doc-badge { text-align: center; margin-bottom: 10px; }
  .doc-badge span {
    display: inline-block; padding: 6px 24px; border-radius: 999px;
    font-size: 10px; font-weight: 700; letter-spacing: 0.18em; text-transform: uppercase;
    background: rgba(var(--lh-accent-rgb), 0.05); color: var(--lh-accent);
    border: 1px solid rgba(var(--lh-accent-rgb), 0.35);
  }
  .doc-title {
    text-align: center; font-size: 16px; font-weight: 700; color: #0f172a;
    margin: 0 0 4px; text-transform: uppercase; letter-spacing: 0.14em;
  }
  .doc-number { text-align: center; font-size: 11px; color: #64748b; margin-bottom: 20px; line-height: 1.5; letter-spacing: 0.03em; }
  .section { margin-bottom: 18px; }
  .section-title {
    font-size: 10px; font-weight: 700; color: var(--lh-accent);
    text-transform: uppercase; letter-spacing: 0.14em;
    margin-bottom: 9px; padding-bottom: 5px; border-bottom: 1px solid #e8edf3;
  }
  .patient-info {
    background: #fafbfd; border: 1px solid #e5eaf1; border-left: 3px solid var(--lh-accent);
    border-radius: 6px; padding: 14px 18px; margin-bottom: 20px;
    font-size: 12.5px; line-height: 1.6; color: #334155;
  }
  .patient-info strong { color: var(--lh-accent); font-weight: 600; }
  .patient-info--detailed { padding: 16px 20px; }
  .patient-info-heading {
    font-size: 10px; font-weight: 700; color: var(--lh-accent);
    text-transform: uppercase; letter-spacing: 0.14em;
    margin-bottom: 11px; padding-bottom: 7px; border-bottom: 1px solid #e5eaf1;
  }
  .patient-info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 8px 24px; }
  .patient-info-row--full { grid-column: 1 / -1; }
  .attachment-block { margin-bottom: 26px; page-break-inside: avoid; }
  .attachment-block + .attachment-block { padding-top: 10px; border-top: 1px solid #eef1f6; }
  .attachment-meta { font-size: 9px; font-weight: 700; color: #8492a6; letter-spacing: 0.16em; text-transform: uppercase; margin-bottom: 7px; }
  .attachment-title { margin: 0 0 12px; font-size: 16px; font-weight: 700; color: #0f172a; letter-spacing: 0.01em; }
  .attachment-body { font-size: 12.5px; line-height: 1.7; color: #334155; }
  .attachment-body-pre { white-space: pre-wrap; font-family: ui-monospace, 'Cascadia Code', 'Segoe UI Mono', monospace; font-size: 11px; line-height: 1.55; color: #334155; background: #fafbfd; border: 1px solid #e5eaf1; border-radius: 6px; padding: 14px 16px; }
  .attachment-block--embedded .doc-head,
  .attachment-block--embedded .clinic-sheet-letterhead,
  .attachment-block--embedded .header { display: none !important; }
  .attachment-block--embedded .print-container { padding-top: 0 !important; max-width: none !important; }
  /* Documentos embutidos (receituário/atestado/exames vindos do endpoint /print)
     trazem sua própria folha + rodapé de unidades. Neutralizamos a folha interna
     e escondemos o rodapé interno para usar SOMENTE o cabeçalho/rodapé do envelope. */
  .attachment-block--embedded .sheet {
    box-shadow: none !important; border-radius: 0 !important;
    padding: 0 !important; margin: 0 !important; max-width: none !important;
    background: transparent !important;
  }
  .attachment-block--embedded .lh-footer-units { display: none !important; }
  .vitals-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px; }
  .vital-item { background: #fafbfd; border: 1px solid #e5eaf1; border-radius: 6px; padding: 9px; text-align: center; }
  .vital-value { font-size: 16px; font-weight: 700; color: var(--lh-accent); }
  .vital-label { font-size: 10px; color: #8492a6; text-transform: uppercase; letter-spacing: 0.08em; }
  .soap-label { display: inline-block; width: 24px; height: 24px; border-radius: 50%; background: var(--lh-accent); color: white; text-align: center; line-height: 24px; font-weight: 700; font-size: 13px; margin-right: 8px; }
  .sheet-evolution-footer { margin-top: 28px; text-align: center; border-top: 1px solid #e8edf3; padding-top: 12px; page-break-inside: avoid; break-inside: avoid; }
  .sheet-evolution-signature-rule { width: 250px; border-top: 1px solid #334155; margin: 18px auto 4px; }
  .prof-sig-print { text-align: center; margin-bottom: 6px; }
  .prof-sig-print img { max-height: 72px; max-width: 320px; object-fit: contain; display: inline-block; vertical-align: bottom; }
  ${CLINIC_DOC_SIGNATURE_CSS}
  .lh-professionals { display: flex; flex-wrap: wrap; gap: 8px 0; margin: 0; padding: 0; border: none; }
  .lh-prof { font-size: 10px; line-height: 1.45; color: #475569; padding-right: 22px; }
  .lh-prof + .lh-prof { padding-left: 22px; border-left: 1px solid #e8edf3; }
  .lh-prof-name { font-weight: 700; color: #0f172a; font-size: 11.5px; letter-spacing: 0.02em; }
  .lh-prof-sub { color: var(--lh-accent); font-size: 8.5px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.12em; margin-top: 2px; }
  .lh-prof-reg { color: #8492a6; font-size: 9px; letter-spacing: 0.05em; margin-top: 1px; }
  .lh-footer-units {
    margin-top: 20px; padding-top: 12px; border-top: 1px solid #e8edf3;
    display: flex; flex-wrap: wrap; gap: 10px 0; justify-content: flex-start;
    position: relative;
    page-break-before: avoid; break-before: avoid-page;
    page-break-inside: avoid; break-inside: avoid;
  }
  /* Eco da barra do cabeçalho — fecha a moldura visual da folha. */
  .lh-footer-units::before {
    content: ''; position: absolute; top: -2px; left: 0; width: 64px; height: 3px;
    background: var(--lh-accent); border-radius: 2px;
  }
  .lh-unit { font-size: 9px; line-height: 1.6; color: #5b6b80; text-align: left; flex: 1 1 0; min-width: 150px; padding: 0 18px; border-left: 1px solid #e8edf3; }
  .lh-unit:first-child { padding-left: 0; border-left: none; }
  .lh-unit-name { font-weight: 700; color: #0f172a; letter-spacing: 0.12em; text-transform: uppercase; font-size: 8.5px; margin-bottom: 4px; }
  .lh-unit-line { display: flex; align-items: flex-start; gap: 5px; margin-top: 2px; }
  .lh-unit-ico { flex-shrink: 0; display: inline-flex; align-items: center; margin-top: 1px; color: var(--lh-accent); }
  .footer-note {
    margin-top: 18px; padding-top: 10px; border-top: 1px solid #eef1f6;
    font-size: 9px; color: #9aa7b8; text-align: center; line-height: 1.6; letter-spacing: 0.06em;
    page-break-before: avoid; break-before: avoid-page;
  }
  .page-break { page-break-after: always; }
  @media print {
    body { background: #fff; padding: 0; margin: 0; }
    .sheet {
      box-shadow: none; border-radius: 0; max-width: none;
      padding: 0; margin: 0;
    }
    @page { size: A4; margin: 12mm 12mm 14mm 12mm; }
    body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    .clinic-sheet-letterhead {
      page-break-inside: avoid; break-inside: avoid;
      padding-bottom: 10px; margin-bottom: 12px; gap: 16px;
    }
    .doc-head-logo img { max-height: 56px; max-width: 140px; }
    .doc-badge { margin-bottom: 6px; }
    .doc-number { margin-bottom: 10px; }
    .section { margin-bottom: 10px; }
    .patient-info { margin-bottom: 10px; padding: 8px 12px; }
    .attachment-block { page-break-inside: auto; break-inside: auto; }
    .clinic-doc-sign-zone {
      margin-top: 14px !important; padding-top: 6px !important;
      page-break-after: avoid; break-after: avoid-page;
    }
    .clinic-doc-sign-zone--compact { margin-top: 10px !important; }
    .clinic-doc-sign-spacer { min-height: 20px !important; }
    .clinic-doc-sign-spacer--with-img { min-height: 8px !important; }
    .clinic-doc-sign-prof-img-wrap img { max-height: 56px !important; }
    .sheet-evolution-footer {
      margin-top: 12px; padding-top: 8px;
      page-break-after: avoid; break-after: avoid-page;
    }
    .sheet-evolution-signature-rule { margin: 12px auto 4px; }
    .prof-sig-print img { max-height: 56px !important; }
    .prx-validity, .exo-validity { margin-top: 12px !important; }
    .lh-footer-units {
      margin-top: 6px !important; padding-top: 6px !important; gap: 6px 0;
      page-break-before: avoid !important; break-before: avoid-page !important;
    }
    .footer-note {
      margin-top: 8px !important; padding-top: 6px !important;
      page-break-before: avoid; break-before: avoid-page;
    }
  }
`;

export const CLINIC_PRINT_BASE_CSS = scaleDocumentPrintFontSizes(CLINIC_PRINT_BASE_CSS_RAW);

/** Regras extras só para pacotes com várias folhas timbradas */
const MULTI_SHEET_PRINT_EXTRA_CSS = `
  .sheet.sheet--page-break { margin-top: 28px; }
  @media print {
    .sheet.sheet--page-break {
      margin-top: 0;
      /* Folhas após quebra (2ª via, 2º mês, etc.) costumam colar o timbre no
         limite físico da página e cortar o topo da logo — reserva folga extra. */
      padding-top: 6mm;
      page-break-before: always;
      break-before: page;
    }
  }
`;
